import {
  Checkbox,
  createStyles,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core'
import { colorPalette } from '../styleVariables'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { transitionsLens } from '../libs/transitionsLens'

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      fontSize: '1.35rem',
      color: colorPalette.greyDark,
    },
    label: {
      fontSize: '1.35rem',
    },
    check: {
      padding: '3px',
    },
    wrapper: {
      marginBottom: '4rem',
    },
    grid: {
      maxWidth: '744px',
      marginTop: '4px',
      marginBottom: '4px',
      marginLeft: '10px',
      gap: '4px',
    },
    option: {
      width: 'auto',
    },
  })
)

interface Props {
  availableColors: string[]
  onSelectedColors: (colors?: string[]) => void
}

export const SelectTransitions: React.FC<Props> = ({
  availableColors,
  onSelectedColors,
}) => {
  const styles = useStyles()
  const [selectedColors, setSelectedColors] = useState(availableColors)

  const callback = useRef(onSelectedColors)

  const onToggleMain = (
    e: React.ChangeEvent<HTMLInputElement>,
    colors: string[]
  ) => {
    setSelectedColors((c) => {
      const isChecked = e.target.checked

      if (isChecked)
        return [...c, ...colors].reduce<string[]>((acc, color) => {
          if (!acc.includes(color)) {
            acc.push(color)
          }

          return acc
        }, [])
      else {
        return c.filter((x) => !colors.includes(x))
      }
    })
  }

  const onToggleColor = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedColors((c) => {
      const isChecked = e.target.checked
      const value = e.target.value

      return !isChecked ? c.filter((x) => x !== value) : [...c, value]
    })
  }

  const selections = useMemo(
    () => ({
      all: transitionsLens
        .map((x) =>
          x.colors.every((i) => selectedColors.includes(i.colorCode))
            ? x.name
            : undefined
        )
        .filter((x) => !!x),
      some: transitionsLens
        .map((x) =>
          x.colors.some((i) => selectedColors.includes(i.colorCode))
            ? x.name
            : undefined
        )
        .filter((x) => !!x),
    }),
    [selectedColors]
  )

  useEffect(() => {
    callback.current(selectedColors.length > 0 ? selectedColors : undefined)
  }, [selectedColors])

  return (
    <>
      <Typography className={styles.title}>
        Select the Transitions lenses:
      </Typography>
      <Grid container direction="column" className={styles.wrapper}>
        {transitionsLens.map((c) => {
          const isAll = selections.all.includes(c.name)
          const isSome = selections.some.includes(c.name)

          return (
            <Fragment key={c.name}>
              <Grid container alignItems="center">
                <Checkbox
                  className={styles.check}
                  onChange={(e) =>
                    onToggleMain(
                      e,
                      c.colors.map((x) => x.colorCode)
                    )
                  }
                  checked={isAll}
                  indeterminate={isSome && !isAll}
                  color="primary"
                  style={{ color: isSome && !isAll ? colorPalette.blue : '' }}
                />
                <Typography className={styles.label}>{c.name}</Typography>
              </Grid>
              <Grid className={styles.grid} container alignItems="center">
                {c.colors.map((color) => (
                  <Grid
                    key={color.colorCode}
                    className={styles.option}
                    container
                    wrap="nowrap"
                    alignItems="center"
                  >
                    <Checkbox
                      className={styles.check}
                      color="primary"
                      value={color.colorCode}
                      onChange={onToggleColor}
                      checked={selectedColors.includes(color.colorCode)}
                    />
                    <Typography>{color.colorName}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Fragment>
          )
        })}
      </Grid>
    </>
  )
}
